import React from 'react';
import { Controller } from 'react-hook-form';
import { SdfInput, SdfAlert, SdfSelectSimple, SdfTooltip, SdfIcon } from '@synerg/react-components';
import trim from 'lodash/trim';
import { STATES } from './utility';

export default function StepTwo({ control }) {
	const partnerOrganizationValidator = {
		emptyState: value => trim(value).length > 0 || 'Partner organization cannot be empty.',
		maximumLength: value => trim(value).length < 100 || 'Partner organization cannot exceed 100 characters.',
		invalidCharacters: value => !(/[^A-Za-z0-9-., ]/.test(trim(value))) || 'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), comma (,) and spaces'
	};

	const partnerIdValidator = {
		emptyState: value => trim(value).length > 2 || 'Partner ID must be at least 3 characters long',
		invalidCharacters: value => !(/[^A-Za-z0-9-.]/.test(trim(value))) || 'Valid characters permitted are A-Z, a-z, 0-9, period (.) and dash (-)',
	};

	const businessAddressValidator = {
		invalidAddress: value => !(/[^A-Za-z0-9-., ]/.test(trim(value))) || 'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), comma (,) and spaces'
	};

	const cityValidator = {
		invalidCity: value => !(/[^A-Za-z-., ]/.test(trim(value))) || 'Invalid city'
	};

	const isPostalCodeValid = value => {
		const val = trim(value);
		if (val.match(/\D/g) || (val.length && val.length !== 5)) {
			return 'Invalid postal code'
		}
		return true;
	};

	const postalCodeValidator = {
		invalidPostalCode: value => isPostalCodeValid(value)
	};

	const isDomainValid = value => {
		if (!(/^(?!-)([a-z0-9A-Z]\.)*[a-z0-9A-Z-]+\.([a-z0-9A-Z]{2,24})+(\.co\.([a-z0-9A-Z]{2,24})|\.([a-z0-9A-Z]{2,24}))*/.test(trim(value)))) {
			return 'Domain should start with valid characters permitted are A-Z, a-z, 0-9, after period (.) at least two characters should be present.';
		}
		const listOfNotAllowDomains = ['gmail.com', 'hotmail.com', 'aol.com', 'yahoo.com', 'adp.com', 'outlook.com', 'me.com'];
		const isAllowed = listOfNotAllowDomains.indexOf(trim(value));
		return isAllowed !== -1 ? 'Invalid domain' : true;
	};

	const domainValidator = {
		invalidDomain: value => isDomainValid(value)
	};

  const ShowError = ({message}) => {
    return (
      <SdfAlert
          className='mt-1'
          slot="alert"
          size="sm"
          status="error"
          type="inline"
      >
          {message}
      </SdfAlert>
    );
  };


	return (
		<div style={{marginTop: '4rem'}}>
			<span className="text-heading-05 font-bold mb-2	">Partner organization details</span>
			<p className="text-body-02 mb-6">A label marked with an asterisk “<span style={{color:'rgb(156, 37, 20)'}}>*</span>” indicates a required field.</p>

			<section className='form1'>
				<div className='w-full flex gap-4'>
					<Controller
						name='partnerOrganization'
						control={control}
						rules={{
							validate: partnerOrganizationValidator,
							required: 'This field is required'
						}}
						render={ ({field: {value, onChange, onBlur}, fieldState: {error}}) => (
							<SdfInput
								label='Partner Organization (legal name)'
								name='partnerOrganization'
								className={ error ? 'w-2/3' : 'w-2/3 mb-6'}
								value={value}
								state={error ? 'error' : 'normal'}
								onSdfInput={(e) => onChange(e.target.value)}
								onBlur={(e) => onBlur(e.target.value)}
								required='true'
								requiredState='required'
							>
							 {error && error.message && <ShowError message={error.message}/>}
							</SdfInput>
						)}
					/>
					<Controller
						name='clientId'
						control={control}
						rules={{
							validate: partnerIdValidator,
							required: 'This field is required'
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<SdfInput
								label='Partner ID'
								name='clientId'
								className={error ? 'w-4/12' : 'w-4/12 mb-6'}
								value={value}
								state={error ? 'error' : 'normal'}
								onSdfInput={(e) => onChange(e.target.value)}
								required='true'
								requiredState='required'
							>
							 {error && error.message && <ShowError message={error.message}/>}
							</SdfInput>
						)}
					/>
				</div>
				<div className='w-full'>
					<Controller
						name='businessAddress'
						control={control}
						rules={{
							validate: businessAddressValidator,
							required: false
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<SdfInput
								label='Business street address (line one)'
								name='businessAddress'
								className={error ? 'w-full' : 'w-full mb-6'}
								value={value}
								state={error ? 'error' : 'normal'}
								onSdfInput={(e) => onChange(e.target.value)}
							>
							 {error && error.message && <ShowError message={error.message}/>}
							</SdfInput>
						)}
					/>
				</div>

				<div className='w-full'>
					<Controller
						name='businessAddressOptional'
						control={control}
						rules={{
							validate: businessAddressValidator,
							required: false
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<SdfInput
								label='Business street address (line two)'
								name='businessAddressOptional'
								className={error ? 'w-full' : 'w-full mb-6'}
								value={value}
								state={error ? 'error' : 'normal'}
								onSdfInput={(e) => onChange(e.target.value)}
							>
								{error && error.message && <ShowError message={error.message}/>}
							</SdfInput>
						)}
					/>
				</div>

				<div className='w-full flex gap-4'>
					<Controller
						name='city'
						control={control}
						rules={{
							validate: cityValidator,
							required: false
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<SdfInput
								label='City'
								name='city'
								className={error ? 'w-1/2' : 'w-1/2 mb-6'}
								value={value}
								state={error ? 'error' : 'normal'}
								onSdfInput={(e) => onChange(e.target.value)}
							>
								{error && error.message && <ShowError message={error.message}/>}
							</SdfInput>
						)}
					/>
					<Controller
						name='state'
						control={control}
						rules={{
							required: false
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<SdfSelectSimple
								placeholder='Select one'
								label='State'
								name='state'
								className={error ? 'w-1/4' : 'w-1/4 mb-6'}
								value={value || undefined}
								state={error ? 'error' : 'normal'}
								onSdfChange={(e) => onChange(e.target.value)}
								items={STATES}
								filterable={true}
							>
								{error && error.message && <ShowError message={error.message}/>}
							</SdfSelectSimple>
						)}
					/>
					<Controller
						name='postalCode'
						control={control}
						rules={{
							validate: postalCodeValidator,
							required: false
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<SdfInput
								label='Zip Code'
								name='postalCode'
								className={error ? 'w-1/4' : 'w-1/4 mb-6'}
								value={value}
								state={error ? 'error' : 'normal'}
								onSdfInput={(e) => onChange(e.target.value)}
							>
								{error && error.message && <ShowError message={error.message}/>}
							</SdfInput>
						)}
					/>
				</div>
				<div className='w-full flex flex-col'>
					<Controller
						name='domainOptional'
						control={control}
						rules={{
							validate: domainValidator,
							required: 'This field is required'
						}}
						render={ ({field: {value, onChange}, fieldState: {error}}) => (
							<>
								<div className='relative' style={{top:'0.3rem'}}>Domain<div className='inline' style={{color: 'rgb(156, 37, 20)'}}>*</div>
									<SdfTooltip
										className='ml-1'
										attachmentPoint={'right'}
										positionContext={'ELEMENT'}
									>
										<SdfIcon icon='alert-info' size='sm' slot='tooltip-target' />
										<span>
											Enter email domain for the new partner (i.e. acme.com).
										</span>
									</SdfTooltip>
								</div>
								<SdfInput
									label=''
									name='domainOptional'
									className={error ? 'w-full' : 'w-full mb-6'}
									value={value}
									state={error ? 'error' : 'normal'}
									onSdfInput={(e) => onChange(e.target.value)}
									requiredState='required'
								>
									{error && error.message && <ShowError message={error.message}/>}
								</SdfInput>
							</>
						)}
					/>
				</div>

			</section>

		</div>
	)


}
